import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ReactTable from 'react-table'
import { Button, Input } from 'reactstrap'
import moment from 'moment'
import { translations } from 'helpers/react-table'
import { decimalFormat } from 'helpers/formater'
import { getVisibleContracts } from 'reducers/contracts'
import {
  fetchContractsTransactions,
  getTableData,
  getIsLoading,
  getSelectedContractId,
} from 'store/deals'
import { isQualityUser as getIsQualityUser } from 'selectors/user'
import {
  validateMassInternalTransaction,
  sendMassValidationNotification,
} from 'actions/transactions'
import { TableSpinner } from 'components/common/Spinner'
import CSVButton from 'components/common/CSVButton'
import { EnumDealValidationType } from 'types/dealType'

const headerStyle = {
  textTransform: 'uppercase',
}

const columnLeft = {
  headerStyle,
}

const columnRight = {
  headerStyle,
  Cell: (row) => <div style={{ textAlign: 'right' }}>{row.value}</div>,
}

const validText = (status, t) => {
  if (status == 1) return t('execution.dealStatus.1')
  if (status == 2) return t('execution.dealStatus.2')
  if (status == 3) return t('execution.transactionStatus.invalidatedInternal')
  if (status == 4) return t('execution.transactionStatus.validatedClient')
  return ''
}

const DealsTable = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const visibleContracts = useSelector(getVisibleContracts)

  const rawData = useSelector(getTableData)
  const data = React.useMemo(() => {
    return rawData.filter(function (element) {
      return element !== undefined
    })
  }, [rawData])

  const isLoading = useSelector(getIsLoading)

  const [exportData, setExportData] = React.useState(data)

  useEffect(() => {
    setExportData(data)
  }, [data])

  const selectedContractId = useSelector(getSelectedContractId)
  const isQualityUser = useSelector(getIsQualityUser)
  const [selected, setSelected] = React.useState(null)
  const tableRef = useRef(null)

  useEffect(() => {
    const filtered = data.filter(
      (item) => item && Number(item.visualize.contractId) === selectedContractId,
    )
    if (filtered.length > 0 && !filtered.some((item) => item.id === selected)) {
      setSelected(filtered[0]?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selected])

  useEffect(() => {
    dispatch(fetchContractsTransactions(visibleContracts))
  }, [dispatch, visibleContracts])

  const [checkedRows, setCheckedRows] = React.useState([])

  const handleToggleAll = (e) => {
    if (!tableRef.current) return

    const state = tableRef.current.getResolvedState()
    const { sortedData, page, pageSize } = state

    const startIdx = page * pageSize
    const endIdx = startIdx + pageSize
    const currentPageRows = sortedData.slice(startIdx, endIdx)

    const currentPageIds = currentPageRows.map((row) => row._original.visualize.id)

    if (e.target.checked) {
      const newChecked = Array.from(new Set([...checkedRows, ...currentPageIds]))
      setCheckedRows(newChecked)
    } else {
      const newChecked = checkedRows.filter((id) => !currentPageIds.includes(id))
      setCheckedRows(newChecked)
    }
  }

  const handleToggleRow = (id) => {
    setCheckedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    )
  }

  const validateDeals = async () => {
    if (checkedRows.length === 0) {
      alert(t('execution.transactionView.noDealsSelected'))
      return
    }

    const idsToSend = checkedRows.filter((id) => {
      const matchedRow = data.find((item) => item.visualize.id === id)
      return matchedRow && Number(matchedRow.internalValidation?.Status) === 1
    })

    if (idsToSend.length === 0) {
      alert(t('execution.transactionView.invalidDealsSelected'))
      return
    }

    // Show confirm popup
    const isConfirmed = window.confirm(t('execution.transactionView.confirmValidation'))

    const notificationPayloads = checkedRows.reduce((acc, id) => {
      const matchedRow = data.find((item) => item.visualize.id === id)

      if (matchedRow && Number(matchedRow.internalValidation?.Status) === 1) {
        acc.push({
          OrderId: matchedRow.visualize.id,
          ContractId: matchedRow.visualize.contractId,
          ValidationStatus: matchedRow.internalValidation.Status,
          DealType: EnumDealValidationType.InternalValidation,
        })
      }

      return acc
    }, [])

    if (notificationPayloads.length === 0) {
      alert(t('execution.transactionView.invalidDealsSelected'))
      return
    }

    if (isConfirmed) {
      // Send mass validation
      dispatch(validateMassInternalTransaction(idsToSend))

      // Send mass validation notification
      dispatch(sendMassValidationNotification(notificationPayloads))

      // Refresh the table
      dispatch(fetchContractsTransactions(visibleContracts))

      setCheckedRows([])
    }
  }

  const columns = [
    {
      Header: (
        <div style={{ textAlign: 'center' }}>
          <Input type="checkbox" onChange={handleToggleAll} checked={checkedRows.length > 0} />
        </div>
      ),
      id: 'checkbox',
      accessor: '',
      sortable: false,
      filterable: false,
      width: 50,
      Cell: (row) => (
        <div style={{ textAlign: 'center' }}>
          <Input
            type="checkbox"
            checked={checkedRows.includes(row.original.visualize.id)}
            onChange={() => handleToggleRow(row.original.visualize.id)}
          />
        </div>
      ),
    },
    {
      Header: t('deals.table.contract'),
      accessor: 'contract',
      ...columnLeft,
      Cell: (row) => (
        <div>
          <span style={{ marginRight: 10 }}>
            {row.original.nrjId === 2 && <i className="icon-elec" />}
            {row.original.nrjId === 1 && <i className="icon-gaz" />}
          </span>
          {row.value}
        </div>
      ),
    },
    {
      Header: t('deals.table.businessUnit'),
      accessor: 'businessUnit',
      ...columnLeft,
      width: 120,
    },
    {
      Header: t('deals.table.parcel'),
      accessor: 'parcel',
      ...columnLeft,
      width: 80,
    },
    {
      Header: t('deals.table.product'),
      accessor: 'product',
      ...columnLeft,
    },
    {
      Header: t('deals.table.date'),
      accessor: 'date',
      width: 120,
      ...columnLeft,
      Cell: (row) => <div style={{ textAlign: 'center' }}>{row.original.dateFormatted}</div>,
      filterMethod: (filter, row) => {
        const a = String(row._original.dateFormatted)
        const b = filter.value
        return a.includes(b)
      },
      sortMethod: (a, b, desc) => {
        // force null and undefined to the bottom
        a = a === null || a === undefined ? '' : moment(a).format('x')
        b = b === null || b === undefined ? '' : moment(b).format('x')
        return a > b ? 1 : a < b ? -1 : 0
      },
    },
    {
      Header: t('deals.table.volume'),
      accessor: 'volume',
      width: 120,
      ...{ headerStyle },
      sortable: false,
      filterable: false,
      Cell: (row) => (
        <div style={{ textAlign: 'right' }}>
          {`${decimalFormat(row.value.value)} ${row.value.unit}`}
        </div>
      ),
    },
    {
      Header: t('deals.table.price'),
      accessor: 'price',
      ...columnRight,
      sortable: false,
      filterable: false,
      Cell: (row) => (
        <div style={{ textAlign: 'right' }}>
          {`${decimalFormat(row.value.value, { digitCount: 4, force: true })} ${row.value.unit}`}
        </div>
      ),
    },
    {
      Header: t('execution.transactionView.internalValid'),
      accessor: 'internalValidation.Status',
      ...columnRight,
      sortable: true,
      filterable: true,
      filterMethod: (filter, row) => {
        if (filter.value === 'all') {
          return true
        }
        return row[filter.id] == filter.value
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(event) => onChange(event.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : 'all'}
        >
          <option value="all">{t('execution.dealStatus.all')}</option>
          <option value="1">{t('execution.dealStatus.1')}</option>
          <option value="2">{t('execution.dealStatus.2')}</option>
          <option value="3">{t('execution.dealStatus.3')}</option>
        </select>
      ),
      Cell: (row) => (
        <div style={{ textAlign: 'right' }}>{t(`execution.dealStatus.${row.value}`)}</div>
      ),
    },
    {
      Header: t('execution.transactionView.clientValid'),
      accessor: 'clientValidation.Status',
      ...columnRight,
      sortable: true,
      filterable: true,
      filterMethod: (filter, row) => {
        if (filter.value === 'all') {
          return true
        }
        return row[filter.id] == filter.value
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(event) => onChange(event.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : 'all'}
        >
          <option value="all">{t('execution.dealStatus.all')}</option>
          <option value="1">{t('execution.dealStatus.1')}</option>
          <option value="2">{t('execution.dealStatus.2')}</option>
          <option value="3">{t('execution.dealStatus.3')}</option>
        </select>
      ),
      Cell: (row) => (
        <div style={{ textAlign: 'right' }}>{t(`execution.dealStatus.${row.value}`)}</div>
      ),
    },
    {
      Header: null,
      accessor: 'visualize',
      width: 50,
      headerStyle,
      filterable: false,
      sortable: false,
      Cell: (row) => {
        const { contractId, lotId, year, id } = row.value
        return (
          <div style={{ textAlign: 'center' }}>
            <Button
              className="btn-table active"
              onClick={() => {
                setSelected(row.original.id)
                props.onVisualize(Number(contractId), Number(lotId), year, Number(id))
              }}
            >
              <i className="icon-eye" style={{ cursor: 'default' }} />
            </Button>
          </div>
        )
      },
    },
  ]

  const handleTableChange = () => {
    setTimeout(() => {
      if (tableRef.current) {
        const { sortedData } = tableRef.current.getResolvedState()
        setExportData(sortedData.map((row) => row._original))
      }
    }, 0)
  }

  const csvColumns = [
    t('deals.table.contract'),
    t('deals.table.businessUnit'),
    t('deals.table.parcel'),
    t('deals.table.product'),
    t('deals.table.date'),
    t('deals.table.volume'),
    t('deals.table.price'),
    t('execution.transactionView.internalValid'),
    t('execution.transactionView.clientValid'),
  ]

  const csvData = [
    csvColumns,
    ...exportData.map((row) => {
      return [
        row.contract,
        row.businessUnit,
        row.parcel ?? '',
        row.product,
        row.dateFormatted,
        `${row.volume.value} ${row.volume.unit}`,
        `${row.price.value} ${row.price.unit}`,
        validText(row.internalValidation?.Status, t),
        validText(row.clientValidation?.Status, t),
      ]
    }),
  ]

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <CSVButton
          className="rounded"
          data={csvData}
          filename={`transactions-${moment().format('YYYYMMDDHHmm')}.csv`}
          disabled={isLoading}
        />
      </div>
      <div className="deals-table">
        <ReactTable
          ref={tableRef}
          loading={isLoading}
          data={data}
          columns={columns}
          defaultPageSize={20}
          className="-striped"
          LoadingComponent={TableSpinner}
          filterable={true}
          onFilteredChange={handleTableChange}
          defaultFilterMethod={(filter, row) => {
            const a = String(row[filter.id]).toLowerCase().replace(/\s\s+/g, ' ')
            const b = filter.value.toLowerCase()
            return a.includes(b)
          }}
          sortable={true}
          defaultSorted={[
            {
              id: 'contract',
              desc: false,
            },
            {
              id: 'parcel',
              desc: false,
            },
            {
              id: 'product',
              desc: false,
            },
            {
              id: 'date',
              desc: false,
            },
          ]}
          {...translations(t)}
          getTrProps={(state, rowInfo) => {
            return {
              style: {
                background: rowInfo?.original.id === selected ? 'rgba(33, 33, 90, .2)' : 'unset',
              },
            }
          }}
        />
        <div className="d-flex justify-content-end mt-3">
          <Button
            className="ml-auto mt-3"
            color="primary"
            onClick={validateDeals}
            disabled={checkedRows.length === 0 || !isQualityUser}
          >
            {t('execution.transactionView.validateButton')}
          </Button>
        </div>
      </div>
    </>
  )
}

export default DealsTable
